import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/api-client';
import { logger } from '@/lib/default-logger';
import { toast } from '@/components/core/toaster';
import { Option } from '@/components/customTable/types';
import { contactsKeys } from './use-contacts';
import { conversationTemplatesKeys } from './use-conversation-templates';

export interface Field {
  id: string;
  name: string;
  label: string;
  description: string;
  type: string;
  options: Option[];
  required: boolean;
  system: boolean;
}

export interface FieldsResponse {
  data: Field[];
  total: number;
}

export interface FieldFormData {
  name: string;
  type: string;
  required: boolean;
  label: string;
  description?: string;
  options?: Record<string, any>;
}

export const fieldsKeys = {
  all: ['fields'] as const,
  lists: () => [...fieldsKeys.all, 'list'] as const,
  list: (query?: string) => [...fieldsKeys.lists(), { query }] as const,
  details: () => [...fieldsKeys.all, 'detail'] as const,
  detail: (id: string) => [...fieldsKeys.details(), id] as const,
};

export function useFields() {
  return useQuery({
    queryKey: fieldsKeys.lists(),
    queryFn: async () => {
      const response = await apiClient.get('/crm/fields');
      return response.data.filter((field: Field) => !field?.system);
    },
  });
}

export function useField(id: string) {
  return useQuery({
    queryKey: fieldsKeys.detail(id),
    queryFn: async (): Promise<Field> => {
      const response = await apiClient.get(`/crm/fields/${id}`);
      return response.data;
    },
    enabled: !!id,
  });
}

export function useCreateField() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: FieldFormData): Promise<Field> => {
      const response = await apiClient.post('/crm/fields', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: fieldsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: contactsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: conversationTemplatesKeys.lists() });
      toast.success('Field created successfully');
    },
    onError: (error: any) => {
      logger.error(error);
      const errorMessage = error?.response?.data?.message;
      toast.error(Array.isArray(errorMessage) ? errorMessage[0] : errorMessage || 'Something went wrong!');
    },
  });
}

export function useUpdateField() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: FieldFormData }): Promise<Field> => {
      const response = await apiClient.put(`/crm/fields/${id}`, data);
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(fieldsKeys.detail(variables.id), data);
      queryClient.invalidateQueries({ queryKey: fieldsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: contactsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: conversationTemplatesKeys.lists() });
      toast.success('Field updated successfully');
    },
    onError: (error: any) => {
      logger.error(error);
      const errorMessage = error?.response?.data?.message;
      toast.error(Array.isArray(errorMessage) ? errorMessage[0] : errorMessage || 'Something went wrong!');
    },
  });
}

export function useDeleteField() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      await apiClient.delete(`/crm/fields/${id}`);
    },
    onSuccess: (_, id) => {
      queryClient.removeQueries({ queryKey: fieldsKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: fieldsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: contactsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: conversationTemplatesKeys.lists() });
      toast.success('Field deleted successfully');
    },
    onError: (error: any) => {
      logger.error(error);
      toast.error('Something went wrong!');
    },
  });
}
